import { Component, OnInit, Input, SimpleChanges, Output, EventEmitter, forwardRef } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { 
    IonGrid, IonRow, IonCol, IonSpinner, ToastController
} from '@ionic/angular/standalone';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import moment from 'moment';

import { 
    MarvButtonComponent, MarvSelectComponent, MarvSelectItem, MarvInputDateComponent, MarvToggleComponent,
    MarvCheckboxComponent, MarvSectionTitleComponent, MarvMessageComponent, MarvSubItemComponent
} from '@marv/components';
import { MarvDateService } from '@marv/services';
import { MarvDateToStringPipe } from '@marv/pipes';
import { PermanentAgentService, PermanentPlanningService } from '@app/services';
import { PearlsPlanningDeleteAgent } from '@app/models';

@Component({
    selector: 'app-permanent-planning-delete',
    templateUrl: './delete.component.html',
    styleUrls: ['./delete.component.scss'],
    imports: [
        IonGrid, IonRow, IonCol, IonSpinner, TranslatePipe, ReactiveFormsModule, MarvDateToStringPipe,
        forwardRef(() => MarvButtonComponent), forwardRef(() => MarvSelectComponent), forwardRef(() => MarvInputDateComponent), 
        forwardRef(() => MarvToggleComponent), forwardRef(() => MarvCheckboxComponent), forwardRef(() => MarvSectionTitleComponent),
        forwardRef(() => MarvMessageComponent), forwardRef(() => MarvSubItemComponent)
    ]
})
export class PermanentPlanningDeleteComponent  implements OnInit {

    @Input({alias : 'operationId', required : true}) operationId: number;
    @Input({alias : 'agentId', required : false}) agentId: number;
    @Output() onDeletePermanentPlanning = new EventEmitter<boolean>();
    
    isFetchingDelete: boolean = false;
    isFetchingPlanning: boolean = false;
    msgError: string|null = null;
    permanentPlanningDeleteForm: FormGroup = new FormGroup({
        startDate: new FormControl(MarvDateService.GetFirstDayOfWeek(), {nonNullable: true, validators: [Validators.required]}),
        endDate: new FormControl(MarvDateService.GetLastDayOfWeek(moment().add(4, 'weeks').toDate()), {nonNullable: true, validators: [Validators.required]}),
        agentsId: new FormControl([], {nonNullable: true, validators: [Validators.required]}),
        includeEvents: new FormControl(true, {nonNullable: true}),
        checkAllItems: new FormControl(false, {nonNullable: true})
    });
    agentsItems: Array<MarvSelectItem> = []; 
    planningItems: Array<PearlsPlanningDeleteAgent> = [];
    checkAllSubscription: Subscription;
    checkAllAgentSubscription: Map<number, Subscription> = new Map();
    
    constructor(
        public translateService: TranslateService,
        private toastController: ToastController,
        private permanentAgentService: PermanentAgentService,
        private permanentPlanningService: PermanentPlanningService
    ) { }

    ngOnInit() {
        this.permanentPlanningDeleteForm.controls['checkAllItems'].valueChanges.subscribe(value => { 
            this.checkAll();
        });
    }
    
    ngOnDestroy() {    
    
        if(this.checkAllSubscription){
            this.checkAllSubscription.unsubscribe();
        }
    
        this.checkAllAgentSubscription.forEach(s => {
            s.unsubscribe();
        });
        this.checkAllAgentSubscription.clear();
    }
    
    async ngOnChanges(changes: SimpleChanges) {
        if(changes['operationId']){
            let agentsItems: Array<MarvSelectItem> = [];
            if(this.operationId){
                let agents = await this.permanentAgentService.loadPermanentsAgents(this.operationId);
                for(let agent of agents){
                    agentsItems.push(new MarvSelectItem(agent.name,agent.id));
                }
            }
            this.agentsItems = agentsItems;
        }
        
        if(changes['agentId'] && changes['agentId'].currentValue){
            this.permanentPlanningDeleteForm.controls['agentsId'].setValue([this.agentId], {emitEvent: false});
        }
    }
    
    async loadPlanning() {
               
        if(this.permanentPlanningDeleteForm.valid){
            
            this.isFetchingPlanning = true;
            
            let planningItems = await this.permanentPlanningService.loadPlanningItemsToDelete(
                this.operationId,
                this.permanentPlanningDeleteForm.controls['startDate'].value,
                this.permanentPlanningDeleteForm.controls['endDate'].value,
                this.permanentPlanningDeleteForm.controls['agentsId'].value,
                this.permanentPlanningDeleteForm.controls['includeEvents'].value
            );
                        
            this.removeFormControls();
            this.planningItems = planningItems;
            this.addFormControls();
                    
            this.isFetchingPlanning = false;
        }
        else{
            for (var controlName in this.permanentPlanningDeleteForm.controls) {
                if(!this.permanentPlanningDeleteForm.controls[controlName].valid) {
                    this.permanentPlanningDeleteForm.controls[controlName].markAsTouched();
                }
            }
        }            
        
    }
    
    addFormControls(){
        for(let agent of this.planningItems){
            this.permanentPlanningDeleteForm.addControl('checkAllAgent_'+agent.salesRepId, new FormControl(false), {emitEvent: false});
            for(let item of agent.items){
                this.permanentPlanningDeleteForm.addControl('checkItem_'+item.id, new FormControl(false), {emitEvent: false});
            }
            this.checkAllAgentSubscription.set(agent.salesRepId, 
                this.permanentPlanningDeleteForm.controls['checkAllAgent_'+agent.salesRepId].valueChanges.subscribe(value => { 
                    this.checkAgentItems(agent);
                })
            );
        }
    }
    
    removeFormControls(){
        for(let agent of this.planningItems){
            let s = this.checkAllAgentSubscription.get(agent.salesRepId);
            if(s){
                s.unsubscribe();
                this.checkAllAgentSubscription.delete(agent.salesRepId);
            }
            this.permanentPlanningDeleteForm.removeControl('checkAllAgent_'+agent.salesRepId, {emitEvent: false});
            for(let item of agent.items){
                this.permanentPlanningDeleteForm.removeControl('checkItem_'+item.id, {emitEvent: false});
            }
        }
    }
    
    checkAll() {
        let check = this.permanentPlanningDeleteForm.controls['checkAllItems'].value;
        for(let agent of this.planningItems){
            this.permanentPlanningDeleteForm.controls['checkAllAgent_'+agent.salesRepId].setValue(check);
            this.checkAgentItems(agent);
        }
    }
    
    checkAgentItems(agent: PearlsPlanningDeleteAgent) {
        let check = this.permanentPlanningDeleteForm.controls['checkAllAgent_'+agent.salesRepId].value;
        for(let item of agent.items){
            this.permanentPlanningDeleteForm.controls['checkItem_'+item.id].setValue(check, {emitEvent: false});
        }
    }
    
    resetForm() {
        this.msgError = null;     
        this.removeFormControls();  
        this.planningItems = []; 
        this.permanentPlanningDeleteForm.reset();        
    }

    async delete() {
        this.msgError = null;  
        if(this.planningItems.length > 0){
            this.isFetchingDelete = true;
            
            let storeCheckIds: Array<number> = [];
            let eventIds: Array<number> = [];
            
            for(let agent of this.planningItems){
                for(let item of agent.items){
                    if(this.permanentPlanningDeleteForm.controls['checkItem_'+item.id].value){
                        if(item.type === 'storecheck'){
                            storeCheckIds.push(item.id);
                        }
                        else if(item.type === 'event'){
                            eventIds.push(item.id);
                        }
                    }
                }
            }
            
            if(storeCheckIds.length > 0 || eventIds.length > 0){
                let statusDelete = await this.permanentPlanningService.deletePlanning(this.operationId, storeCheckIds, eventIds);
                if(statusDelete){
                    this.onDeletePermanentPlanning.emit(true);
                    this.resetForm();
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('MSG_SUCCESS_DELETE_PLANNING'),
                        duration: 6000,
                        position: 'top',
                        color: 'success'
                    });
                    await toast.present();
                }
                else{
                    const toast = await this.toastController.create({
                        message: this.translateService.instant('MSG_ERROR_DELETE_PLANNING'),
                        duration: 6000,
                        position: 'top',
                        color: 'danger'
                    });
                    await toast.present();
                }                
            }
            else{
                this.msgError = this.translateService.instant('MSG_ERROR_DELETE_PLANNING_ITEMS');
            }
            
            this.isFetchingDelete = false;
        }        
    }
    
    cancel() {
        this.resetForm();
        this.onDeletePermanentPlanning.emit(false);
    }
}
